import { ListPlus, TriangleAlertIcon } from 'lucide-react';
import { useMemo } from 'react';

import { cn } from '@crossnokaye/ux-common';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crossnokaye/ux-common/ckds';

import { TableColumnConfig } from '@repo/sdk/models/components';

import { DiscardChangesDialog } from '@/components/change-dialogs/discard-changes-dialog';
import { DropdownButton } from '@/ux-common/components/dropdown-button/dropdown-button';
import { Icon } from '@/ux-common/components/icon';
import { Inline } from '@/ux-common/components/inline/inline';
import { withTooltip } from '@/ux-common/components/tooltip/with-tooltip';

import { useEquipmentBulkEditColumnSelection } from './use-equipment-bulk-edit-column-selection';

const IconWithTooltip = withTooltip(Icon);
const DropdownMenuCheckboxItemWithTooltip = withTooltip(DropdownMenuCheckboxItem);

interface EquipmentBulkEditColumnSelectorProps {
  columns: TableColumnConfig[];
  selectedColumnIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  dirtyColumnIds?: Set<string>;
  disabledColumnIds?: Set<string>;
  disabledColumnReasons?: Map<string, string>;
  onDiscardColumns?: (columnIds: string[]) => void;
  className?: string;
}

export const EquipmentBulkEditColumnSelector = ({
  columns,
  selectedColumnIds,
  onSelectionChange,
  dirtyColumnIds,
  disabledColumnIds,
  disabledColumnReasons,
  onDiscardColumns,
  className,
}: EquipmentBulkEditColumnSelectorProps) => {
  const { handleToggle, getColumnLabel, discardDialog } = useEquipmentBulkEditColumnSelection({
    columns,
    selectedColumnIds,
    onSelectionChange,
    dirtyColumnIds,
    disabledColumnIds,
    onDiscardColumns,
  });

  // Build the dialog description with column list
  const discardDescription = useMemo(() => {
    if (discardDialog.columnsToDiscard.length === 0) return null;
    return (
      <div>
        <p>
          Columns being removed have unapplied changes. Values will be discarded for the following
          columns:
        </p>
        <ul className="list-disc pl-6 mt-2">
          {discardDialog.columnsToDiscard.map((id) => (
            <li key={id}>{getColumnLabel(id)}</li>
          ))}
        </ul>
      </div>
    );
  }, [discardDialog.columnsToDiscard, getColumnLabel]);

  // Filter out columns that are not editable.
  const columnCheckboxes = useMemo(() => {
    return columns
      .filter((column) => column.canEdit)
      .map((column) => {
        const isDirty = dirtyColumnIds?.has(column.columnId) ?? false;
        const isDisabled = disabledColumnIds?.has(column.columnId) ?? false;
        const disabledReason = disabledColumnReasons?.get(column.columnId);
        return (
          <DropdownMenuCheckboxItemWithTooltip
            checked={selectedColumnIds.includes(column.columnId)}
            className="[&>div:first-child]:flex-1 data-[disabled]:pointer-events-auto"
            disabled={isDisabled}
            key={column.columnId}
            nestedDepth={1}
            onCheckedChange={() => handleToggle(column.columnId)}
            tooltipArrow
            tooltipContent={isDisabled ? disabledReason : undefined}
          >
            <div className="flex items-center justify-between">
              <span>{column.label ?? column.columnId}</span>
              {isDirty && (
                <IconWithTooltip
                  className="icon-sm icon-square icon-caution"
                  tooltipArrow
                  tooltipContent="Changes applied"
                >
                  <TriangleAlertIcon className="glyph glyph-sm" aria-hidden="true" />
                </IconWithTooltip>
              )}
            </div>
          </DropdownMenuCheckboxItemWithTooltip>
        );
      });
  }, [
    columns,
    dirtyColumnIds,
    disabledColumnIds,
    disabledColumnReasons,
    selectedColumnIds,
    handleToggle,
  ]);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <DropdownButton className={cn('btn btn-default', className)}>
            <Inline>
              <ListPlus className="glyph glyph-sm text-black" aria-hidden="true" />
              Select Columns
            </Inline>
          </DropdownButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="min-w-72 max-h-[50vh] overflow-y-auto"
          withPortal
          align="start"
        >
          <DropdownMenuLabel>Select Columns</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {columnCheckboxes}
        </DropdownMenuContent>
      </DropdownMenu>

      <DiscardChangesDialog
        open={discardDialog.open}
        onOpenChange={discardDialog.onOpenChange}
        onConfirm={discardDialog.onConfirm}
        title="Discard Column Changes?"
        description={discardDescription}
        cancelLabel="KEEP COLUMNS"
        confirmLabel="DISCARD CHANGES"
      />
    </>
  );
};
